//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Typography } from '@mui/material'
import { PoolRolloverActorToString, PoolRolloverState, PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement, useEffect } from 'react'

import ButtonModal from '@react/components/ButtonModal'
import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import { PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'

import { erc20WeiToCommaDecString, rolloverActorToRoleEnum, toHourString } from '@shared/conversions'

import AdminApproveLoanForm from '@react/admin/AdminApproveLoanForm'
import AdminCompleteRolloverApproveWithdrawsForm from '@react/admin/AdminCompleteRolloverApproveWithdrawsForm'
import AdminDailyAccrualForm from '@react/admin/AdminDailyAccrualForm'
import AdminFeeWithdrawalForm from '@react/admin/AdminFeeWithdrawForm'
import AdminInitiateRolloverForm from '@react/admin/AdminInitiateRolloverForm'
import LoanCreateForm from '@react/components/loans/LoanCreateForm'
import { PoolCrossChainTransferCTO, TransferStepNumbers } from '@shared/PoolOffchainDataCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import BorrowerRolloverFinalizeForm from './BorrowerRolloverFinalizeForm'
import BorrowerSetFlexInterestRateForm from './BorrowerSetFlexInterestRateForm'

import { poolCrossChainTransferFetch } from '@react/fetchers/PoolFetchers'
import AdminApproveTransferDestination from '@react/transfer/AdminApproveTransferDestination'
import AdminApproveTransferSource from '@react/transfer/AdminApproveTransferSource'
import AdminTransferMintDestination from '@react/transfer/AdminTransferMintDestination'
import { logOT } from '@shared/loggingShared'
import AdminPoolRemoveForm from '@react/admin/AdminPoolRemoveForm'

export default function BorrowerAdminPoolActionModals(props: { poolAddr: EthAddr; poolName: string }): ReactElement {
  const { poolAddr, poolName } = props
  const [poolCrossChainTransferCTO, setPoolCrossChainTransferCTO] = React.useState<
    PoolCrossChainTransferCTO | undefined
  >(undefined)
  const [poolCrossChainTransferCTOFetched, setPoolCrossChainTransferCTFetched] = React.useState<boolean>(false)

  const {
    stageConfiguration: { stageName, restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum },
    chainConfiguration: { chainId, providerReadOnly },
  } = useOtChainState()

  const chainTimestamp = useOtChainTimestamp()

  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : undefined
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate
      ? (poolBaseOverviewCTO as PoolFlexOverviewCTO)
      : undefined

  const needsAccrual = (nowTime: number, poolOverviewCTO: PoolFlexOverviewCTO): boolean => {
    const toMidnightJS = (date: Date): Date => {
      const d1 = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
      const d2 = new Date(d1.getTime() - date.getTimezoneOffset() * 60000)

      return d2
    }

    let midnight = toMidnightJS(new Date(nowTime)).getTime()

    if (nowTime % (3600 * 24 * 1000) < poolOverviewCTO.closeOfDepositTime * 1000) {
      midnight -= 3600 * 24 * 1000
    }

    if (midnight <= poolOverviewCTO.lastDayAccrued * 1000) {
      return false
    }

    return true
  }
  useEffect(() => {
    if (poolAddr && chainId && restAPIKey && serverApiBaseUrl && providerReadOnly) {
      poolCrossChainTransferFetch(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_poolCrossChainTransferCTO: PoolCrossChainTransferCTO | undefined) => {
          setPoolCrossChainTransferCTFetched(true)
          logOT.info('poolCrossChainTransferCTO fetched ', serverApiBaseUrl, poolAddr, _poolCrossChainTransferCTO)
          setPoolCrossChainTransferCTO(_poolCrossChainTransferCTO)
        })
        .catch(() => {
          console.error('Fetch _poolOffchainDataCTO error')
          setPoolCrossChainTransferCTFetched(true)
          setPoolCrossChainTransferCTO(undefined)
        })
    } else {
      setPoolCrossChainTransferCTO(undefined)
      setPoolCrossChainTransferCTFetched(false)
    }
  }, [chainId, serverApiBaseUrl, providerReadOnly, poolAddr])
  useEffect(() => {
    if (serverApiBaseUrl && restAPIKey && poolAddr) {
      window.addEventListener(
        'modalClose',
        () => {
          poolCrossChainTransferFetch(serverApiBaseUrl, restAPIKey, poolAddr)
            .then((_poolCrossChainTransferCTO: PoolCrossChainTransferCTO | undefined) => {
              setPoolCrossChainTransferCTO(_poolCrossChainTransferCTO)
            })
            .catch((e: any) => {
              console.error('Fetch _poolOffchainDataCTO error', e)
            })
        },
        false
      )
    }

    return () => {
      window.removeEventListener('modalClose', () => {}, false)
    }
  }, [])

  logOT.debug('BorrowerAdminPoolActionModals canRemovePool', roleEnum, stageName)

  if (!poolBaseOverviewCTO) {
    return <div></div>
  }
  if (
    poolOverviewCTO &&
    rolloverActorToRoleEnum(poolOverviewCTO.rolloverActor) !== roleEnum &&
    roleEnum === RoleEnum.LENDER
  ) {
    return <div></div>
  }

  const isAnotherRoleString =
    poolOverviewCTO && rolloverActorToRoleEnum(poolOverviewCTO.rolloverActor) !== roleEnum
      ? `Action needed by ${PoolRolloverActorToString[poolOverviewCTO.rolloverActor]} `
      : undefined

  const canApproveTransferSource =
    roleEnum === RoleEnum.POOL_ADMIN &&
    poolCrossChainTransferCTOFetched &&
    !poolCrossChainTransferCTO &&
    (poolFlexOverviewCTO ||
      (poolOverviewCTO &&
        (poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.CreateNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.ApproveNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.RolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.LateRolloverInitiate)))

  const canApproveTransferDestination =
    roleEnum === RoleEnum.POOL_ADMIN &&
    poolCrossChainTransferCTOFetched &&
    poolCrossChainTransferCTO?.destinationPoolAddr === poolAddr &&
    poolCrossChainTransferCTO?.transferStep === TransferStepNumbers.Source_Approved &&
    (poolFlexOverviewCTO ||
      (poolOverviewCTO &&
        (poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.CreateNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.ApproveNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.RolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.LateRolloverInitiate)))

  const canMintTransferDestination =
    roleEnum === RoleEnum.POOL_ADMIN &&
    poolCrossChainTransferCTOFetched &&
    poolCrossChainTransferCTO?.destinationPoolAddr === poolAddr &&
    poolCrossChainTransferCTO?.transferStep === TransferStepNumbers.Source_Burned &&
    (poolFlexOverviewCTO ||
      (poolOverviewCTO &&
        (poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.CreateNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.ApproveNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.RolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.LateRolloverInitiate)))

  const canRemovePool = roleEnum === RoleEnum.POOL_ADMIN
  logOT.debug('BorrowerAdminPoolActionModals canRemovePool', canRemovePool, roleEnum, stageName)

  return (
    <Box
      data-testid={'BorrowerAdminPoolActionModals'}
      px={0}
      py={2}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        direction: 'row',
        gap: 2,
      }}
    >
      {canRemovePool && (
        <ButtonModal title={'Remove Pool'} variant="primary">
          <AdminPoolRemoveForm poolAddr={poolAddr} />
        </ButtonModal>
      )}
      {canApproveTransferSource && (
        <ButtonModal title={'Approve Cross-Vault Transfer'} variant="tertiary">
          <AdminApproveTransferSource poolName={poolName} poolAddr={poolAddr}></AdminApproveTransferSource>
        </ButtonModal>
      )}
      {canApproveTransferDestination && (
        <ButtonModal title={'Approve Cross-Vault Transfer Destination'} variant="tertiary">
          <AdminApproveTransferDestination poolAddr={poolAddr}></AdminApproveTransferDestination>
        </ButtonModal>
      )}

      {canMintTransferDestination && (
        <ButtonModal title={'Mint Cross-Vault Transfer Destination'} variant="tertiary">
          <AdminTransferMintDestination poolAddr={poolAddr}></AdminTransferMintDestination>
        </ButtonModal>
      )}
      {poolOverviewCTO &&
        (poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.RolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.LateRolloverInitiate) && (
          <ButtonModal
            title={'Initiate Rollover'}
            isAnotherRoleString={isAnotherRoleString}
            isLateString={
              poolOverviewCTO.rolloverState === PoolRolloverState.LateRolloverInitiate
                ? ` ${toHourString(poolOverviewCTO.rolloverTimePastActionWindow)} past due hours`
                : undefined
            }
            isEarlyString={
              poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRolloverInitiate
                ? ` ${toHourString(poolOverviewCTO.rolloverTimeToActionWindow)} Early `
                : undefined
            }
            variant="primary"
          >
            <AdminInitiateRolloverForm poolAddr={poolAddr}></AdminInitiateRolloverForm>
          </ButtonModal>
        )}
      {poolOverviewCTO && roleEnum === RoleEnum.POOL_ADMIN && poolOverviewCTO.feeVaultBalance !== '0' && (
        <ButtonModal title={'Withdraw Fees'} variant="primary">
          <AdminFeeWithdrawalForm poolAddr={poolAddr} />
        </ButtonModal>
      )}
      {poolOverviewCTO &&
        poolOverviewCTO.createdLoanState.loanAddr &&
        poolOverviewCTO.createdLoanState.loanAddr !== ETH_ADDR_ZERO &&
        (poolOverviewCTO.rolloverState === PoolRolloverState.ApproveNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.LateApproveNextLoan) && (
          <ButtonModal
            title={'Approve Loan'}
            isAnotherRoleString={isAnotherRoleString}
            isLateString={
              poolOverviewCTO.rolloverState === PoolRolloverState.LateApproveNextLoan
                ? ` ${toHourString(poolOverviewCTO.rolloverTimePastActionWindow)} past due hours`
                : undefined
            }
            variant="primary"
          >
            <AdminApproveLoanForm
              poolAddr={poolAddr}
              loanAddr={poolOverviewCTO.createdLoanState.loanAddr}
            ></AdminApproveLoanForm>
          </ButtonModal>
        )}

      {poolOverviewCTO && poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRepayWithdrawsAndFees && (
        <Typography>
          {isAnotherRoleString} Early Repay to cover withdraws and fees{' '}
          {erc20WeiToCommaDecString(poolOverviewCTO.rolloverNetRepaymentWithFees)}
          {poolBaseOverviewCTO.liquidityTokenSymbol} {toHourString(poolOverviewCTO.rolloverTimeToActionWindow)} Early{' '}
        </Typography>
      )}

      {poolOverviewCTO && poolOverviewCTO.rolloverState === PoolRolloverState.RepayWithdrawsAndFees && (
        <Typography>
          {isAnotherRoleString} Repay to cover withdraws and fees{' '}
          {erc20WeiToCommaDecString(poolOverviewCTO.rolloverNetRepaymentWithFees)}{' '}
          {poolBaseOverviewCTO.liquidityTokenSymbol}
        </Typography>
      )}

      {poolOverviewCTO && poolOverviewCTO.rolloverState === PoolRolloverState.LateRepayWithdrawsAndFees && (
        <Typography>
          {isAnotherRoleString} Late Repay to cover withdraws and fees{' '}
          {erc20WeiToCommaDecString(poolOverviewCTO.rolloverNetRepaymentWithFees)}{' '}
          {poolBaseOverviewCTO.liquidityTokenSymbol} {toHourString(poolOverviewCTO.rolloverTimePastActionWindow)} past
          due{' '}
        </Typography>
      )}

      {poolOverviewCTO &&
        (poolOverviewCTO.rolloverState === PoolRolloverState.EarlyReleaseRedemptions ||
          poolOverviewCTO.rolloverState === PoolRolloverState.ReleaseRedemptions ||
          poolOverviewCTO.rolloverState === PoolRolloverState.LateReleaseRedemptions) && (
          <>
            <ButtonModal
              title={'Complete Rollover, Return Assets to  Vault, & Unlock Withdrawals'}
              tooltip={
                'This will result in the Assets to Return to Vault (including Fees) amount being transferred from the Borrower Vault to the Loan Vault where it can be withdrawn by the Lender(s)'
              }
              isAnotherRoleString={isAnotherRoleString}
              isLateString={
                poolOverviewCTO.rolloverState === PoolRolloverState.LateReleaseRedemptions
                  ? ` ${toHourString(poolOverviewCTO.rolloverTimePastActionWindow)} past due hours`
                  : undefined
              }
              isEarlyString={
                poolOverviewCTO.rolloverState === PoolRolloverState.EarlyReleaseRedemptions
                  ? ` ${toHourString(poolOverviewCTO.rolloverTimeToActionWindow)} Early `
                  : undefined
              }
              variant="primary"
            >
              <AdminCompleteRolloverApproveWithdrawsForm
                poolAddr={poolAddr}
              ></AdminCompleteRolloverApproveWithdrawsForm>
            </ButtonModal>
          </>
        )}
      {poolOverviewCTO &&
        (poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRolloverFinalize ||
          poolOverviewCTO.rolloverState === PoolRolloverState.RolloverFinalize ||
          poolOverviewCTO.rolloverState === PoolRolloverState.LateRolloverFinalize) && (
          <ButtonModal
            title={'Finalize Rollover'}
            isAnotherRoleString={isAnotherRoleString}
            isLateString={
              poolOverviewCTO.rolloverState === PoolRolloverState.LateRolloverFinalize
                ? ` ${toHourString(poolOverviewCTO.rolloverTimePastActionWindow)} past due hours`
                : undefined
            }
            isEarlyString={
              poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRolloverFinalize
                ? ` ${toHourString(poolOverviewCTO.rolloverTimeToActionWindow)} Early `
                : undefined
            }
            variant="primary"
          >
            <BorrowerRolloverFinalizeForm
              poolAddr={poolAddr}
              initialAPR={poolOverviewCTO?.requestedLoanState.indicativeApr ?? 0}
            ></BorrowerRolloverFinalizeForm>
          </ButtonModal>
        )}
      {poolOverviewCTO && poolOverviewCTO.rolloverState === PoolRolloverState.CreateNextLoan && (
        <ButtonModal title={'Create Loan'} isAnotherRoleString={isAnotherRoleString} variant="primary">
          <LoanCreateForm poolAddr={poolAddr} poolOverviewCTO={poolOverviewCTO}></LoanCreateForm>
        </ButtonModal>
      )}

      {poolFlexOverviewCTO && roleEnum === RoleEnum.POOL_ADMIN && (
        <ButtonModal
          title={'Daily Accrual'}
          variant="primary"
          disabled={!needsAccrual(chainTimestamp * 1000, poolFlexOverviewCTO)}
        >
          <AdminDailyAccrualForm poolAddr={poolAddr} />
        </ButtonModal>
      )}
      {poolFlexOverviewCTO && roleEnum === RoleEnum.BORROWER && (
        <ButtonModal title={'Set Interest Rate'} variant="primary">
          <BorrowerSetFlexInterestRateForm poolAddr={poolAddr}></BorrowerSetFlexInterestRateForm>
        </ButtonModal>
      )}
    </Box>
  )
}
